import { api } from './api';

export type AvailabilityEffect = 'allow' | 'deny';

export interface AvailabilityGroup {
  id: number;
  name: string;
  description: string | null;
  source: 'manual' | 'api';
  regionCode: string | null;
  providerKey: string | null;
  aktiv: boolean;
  periodCount?: number | null;
}

export interface AvailabilityGroupPeriod {
  id: number;
  groupId: number;
  title: string | null;
  dateFrom: string;
  dateTo: string;
  timeStart: string | null;
  timeEnd: string | null;
  freq: 'NONE' | 'YEARLY';
  intervalN: number;
  defaultEffect: 'block' | 'allow';
  aktiv: boolean;
}

export interface AvailabilityScopeRule {
  id: number;
  scopeType: 'global' | 'standort' | 'teacher';
  scopeId: number | null;
  groupId: number;
  groupName?: string | null;
  standortName?: string | null;
  lehrerName?: string | null;
  effect: AvailabilityEffect;
  priority: number;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  aktiv: boolean;
}

export interface TeacherAvailabilityOverride {
  id: number;
  lehrerId: number;
  standortId: number | null;
  dateFrom: string;
  dateTo: string;
  timeStart: string | null;
  timeEnd: string | null;
  effect: AvailabilityEffect;
  title: string | null;
  notes: string | null;
  priority: number;
  aktiv: boolean;
}

export interface AvailabilityCheckResult {
  available: boolean;
  severity: string;
  reason: string | null;
  source: string | null;
  details?: Record<string, unknown> | null;
}

export const availabilityApi = {
  listGroups: () => api.get<AvailabilityGroup[]>('/api/availability-groups'),
  createGroup: (payload: Partial<AvailabilityGroup>) =>
    api.post<AvailabilityGroup>('/api/availability-groups', payload),
  updateGroup: (id: number, payload: Partial<AvailabilityGroup>) =>
    api.put<AvailabilityGroup>(`/api/availability-groups/${id}`, payload),
  deleteGroup: (id: number) => api.delete(`/api/availability-groups/${id}`),

  listPeriods: (groupId: number) =>
    api.get<AvailabilityGroupPeriod[]>(`/api/availability-groups/${groupId}/periods`),
  createPeriod: (groupId: number, payload: Partial<AvailabilityGroupPeriod>) =>
    api.post<AvailabilityGroupPeriod>(`/api/availability-groups/${groupId}/periods`, payload),
  updatePeriod: (groupId: number, periodId: number, payload: Partial<AvailabilityGroupPeriod>) =>
    api.put<AvailabilityGroupPeriod>(`/api/availability-groups/${groupId}/periods/${periodId}`, payload),
  deletePeriod: (groupId: number, periodId: number) =>
    api.delete(`/api/availability-groups/${groupId}/periods/${periodId}`),

  importHolidays: (payload: { year: number; regionCode?: string; groupName?: string }) =>
    api.post<{ imported: number; skipped: number; groupId: number }>('/api/availability-groups/import-holidays', payload),

  listScopeRules: () => api.get<AvailabilityScopeRule[]>('/api/availability-scope-rules'),
  createScopeRule: (payload: Partial<AvailabilityScopeRule>) =>
    api.post<AvailabilityScopeRule>('/api/availability-scope-rules', payload),
  updateScopeRule: (id: number, payload: Partial<AvailabilityScopeRule>) =>
    api.put<AvailabilityScopeRule>(`/api/availability-scope-rules/${id}`, payload),
  deleteScopeRule: (id: number) => api.delete(`/api/availability-scope-rules/${id}`),

  getTeacherRules: (wpUserId: number) =>
    api.get<{ groupRules: AvailabilityScopeRule[]; overrides: TeacherAvailabilityOverride[] }>(
      `/api/personen/${wpUserId}/availability-rules`
    ),
  createTeacherGroupRule: (wpUserId: number, payload: { groupId: number; effect: AvailabilityEffect; priority?: number }) =>
    api.post<AvailabilityScopeRule>(`/api/personen/${wpUserId}/availability-rules/groups`, payload),
  deleteTeacherGroupRule: (wpUserId: number, ruleId: number) =>
    api.delete(`/api/personen/${wpUserId}/availability-rules/groups/${ruleId}`),
  createTeacherOverride: (wpUserId: number, payload: Partial<TeacherAvailabilityOverride>) =>
    api.post<TeacherAvailabilityOverride>(`/api/personen/${wpUserId}/availability-rules/overrides`, payload),
  updateTeacherOverride: (wpUserId: number, overrideId: number, payload: Partial<TeacherAvailabilityOverride>) =>
    api.put<TeacherAvailabilityOverride>(`/api/personen/${wpUserId}/availability-rules/overrides/${overrideId}`, payload),
  deleteTeacherOverride: (wpUserId: number, overrideId: number) =>
    api.delete(`/api/personen/${wpUserId}/availability-rules/overrides/${overrideId}`),

  checkSlot: (wpUserId: number, payload: { standortId?: number; date: string; startTime: string; endTime: string }) =>
    api.post<AvailabilityCheckResult>(`/api/personen/${wpUserId}/availability-check`, payload),
};
